import type { SeveritaNotificaApi } from "~~/shared/data-model/notifica_api";

// Funzioni pure per mostrare le notifiche: quando è successo («3 min fa»), il raggruppamento per giorno e il colore della barra di gravità. Come si descrive una notifica (chiavi i18n e icona) sta in `shared/utils/notifica_descrizione.ts`, perché serve anche al server per email e push.

/** Colore della barra di gravità (solo palette: le gravità alte in terracotta; la gravità si legge anche dall'etichetta). */
export const CLASSE_BARRA_GRAVITA: Record<SeveritaNotificaApi, string> = {
  info: "bg-border-strong",
  warn: "bg-text-accent/60",
  error: "bg-accent",
  critical: "bg-accent",
};

const MINUTO = 60;
const ORA = 3600;
const GIORNO = 86400;

/** «ora», «3 minuti fa», «2 ore fa», «ieri», «3 giorni fa»; oltre la settimana la data breve. */
export function tempoRelativo(iso: string, adesso: Date, lingua: string): string {
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return "";
  const secondi = Math.max(0, Math.round((adesso.getTime() - quando.getTime()) / 1000));
  const rtf = new Intl.RelativeTimeFormat(lingua, { numeric: "auto" });
  if (secondi < MINUTO) return rtf.format(0, "second");
  if (secondi < ORA) return rtf.format(-Math.floor(secondi / MINUTO), "minute");
  if (secondi < GIORNO) return rtf.format(-Math.floor(secondi / ORA), "hour");
  if (secondi < 7 * GIORNO) return rtf.format(-Math.floor(secondi / GIORNO), "day");
  return new Intl.DateTimeFormat(lingua, { dateStyle: "short" }).format(quando);
}

export interface GruppoGiorno<T> {
  /** Giorno locale `AAAA-MM-GG`. */
  chiave: string;
  etichetta: string;
  righe: T[];
}

const chiaveGiorno = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Raggruppa per giorno locale (dal più recente, mantenendo l'ordine delle righe); «Oggi» e «Ieri» con le etichette date, gli altri con la data estesa. */
export function raggruppaPerGiorno<T extends { createdAt: string }>(righe: readonly T[], adesso: Date, lingua: string, etichette: { oggi: string; ieri: string }): GruppoGiorno<T>[] {
  const oggi = chiaveGiorno(adesso);
  const ieriData = new Date(adesso);
  ieriData.setDate(ieriData.getDate() - 1);
  const ieri = chiaveGiorno(ieriData);
  const formato = new Intl.DateTimeFormat(lingua, { dateStyle: "long" });
  const gruppi: GruppoGiorno<T>[] = [];
  for (const riga of righe) {
    const data = new Date(riga.createdAt);
    if (Number.isNaN(data.getTime())) continue;
    const chiave = chiaveGiorno(data);
    let gruppo = gruppi.find((g) => g.chiave === chiave);
    if (!gruppo) {
      gruppo = { chiave, etichetta: chiave === oggi ? etichette.oggi : chiave === ieri ? etichette.ieri : formato.format(data), righe: [] };
      gruppi.push(gruppo);
    }
    gruppo.righe.push(riga);
  }
  return gruppi;
}
